import { DocumentTextIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ClinicalNote, Encounter } from "../../shared/types";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useVerification } from "src/contexts/VerificationContext";
import { apiGet, apiPost } from '../../utils/api';
import BlurredCard from "../BlurredCard";
import Loading from "../Loading";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import { ViewableItem, fetchBinaryContent, downloadItem } from "../modals/ItemListModal";
import EmailModal from "../modals/EmailModal";
import Modal from "../modals/Modal";
import { useToast } from "../Toast";
import ActionsMenu from "../ActionsMenu";
import TableBadge from "../table/TableBadge";
import { capitalizeStatus } from "src/utils/utils";
import PractitionerLink from "../table/PractitionerLink";
import { getDateTimeSortValue } from "../table/TableDateTime";
import TableDateTime from "../table/TableDateTime";
import UploadClinicalNoteModal from "../modals/UploadClinicalNoteModal";
import { ActiveTableFilter } from "../ActiveTableFilter";

const statusColorMap: Record<string, string> = {
  current: "bg-emerald-500",
  superseded: "bg-amber-500",
  "entered-in-error": "bg-rose-500",
};

const noteToViewableItem = (note: ClinicalNote): ViewableItem => ({
  name: note.name,
  date: note.date,
  status: note.status,
  contentType: note.contentType,
  binaryUrl: note.url,
});

const noteColumns: StaticDataColumnProps[] = [
  { title: "Type", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Author", sortable: true },
  { title: "Status", sortable: true },
];

const convertNotesToRows = (
  notes: ClinicalNote[],
  isVerified: boolean,
  onView: (note: ClinicalNote) => void,
  onDownload: (note: ClinicalNote) => void,
  onEmail: (note: ClinicalNote) => void,
): StaticDataRow[][] => {
  return notes.map((note) => [
    {
      name: "Type",
      value: note.name,
      element: <span className="font-medium text-gray-900">{note.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(note.date, note.time),
      element: <TableDateTime date={note.date} startTime={note.time} />
    },
    {
      name: "Author",
      value: note.author?.name || "—",
      element: <PractitionerLink name={note.author?.name || "—"} practitionerId={note.author?.practitioner_id} />
    },
    {
      name: "Status",
      value: note.status,
      element: <TableBadge text={capitalizeStatus(note.status)} bgColorClass={statusColorMap[note.status] ?? "bg-gray-500"} />
    },
    {
      name: "Actions",
      value: "",
      element: (
        <ActionsMenu
          actions={[
            {
              id: "view-note",
              label: "View",
              onClick: () => onView(note),
              disabled: !isVerified,
            },
            {
              id: "download-note",
              label: "Download",
              onClick: () => onDownload(note),
              disabled: !isVerified,
            },
            {
              id: "email-note",
              label: "Email",
              onClick: () => onEmail(note),
              disabled: !isVerified,
            },
          ]}
        />
      ),
    },
  ]);
};

const dummyNotes: ClinicalNote[] = [
  {
    id: "1",
    name: "Progress Note",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "current",
    author: {
      practitioner_id: "123",
      name: "Dr. John Doe",
    },
    contentType: "text/html",
    url: "",
  },
  {
    id: "2",
    name: "History & Physical",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "current",
    author: {
      practitioner_id: "456",
      name: "Dr. Jane Doe",
    },
    contentType: "text/html",
    url: "",
  },
];

type NotesCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
  encounterFilter?: Encounter | null;
  onClearEncounterFilter?: () => void;
};

const NotesCard = ({ yearFrom, yearTo, refreshKey, encounterFilter = null, onClearEncounterFilter }: NotesCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId, patientData, loadingPatientData } = useOrganizationContext();
  const { Toast, showToast } = useToast();

  const [ clinicalNotes, setClinicalNotes ] = useState<ClinicalNote[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ viewingNote, setViewingNote ] = useState<ViewableItem | null>(null);
  const [ emailNote, setEmailNote ] = useState<ClinicalNote | null>(null);
  const [ uploadNoteOpen, setUploadNoteOpen ] = useState(false);
  const [ submittingNote, setSubmittingNote ] = useState(false);

  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchNotes = useCallback(async () => {
    if (!organization || !patientId) {
      setClinicalNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = {
        yearFrom,
        yearTo,
        encounterId: encounterFilter?.id || undefined,
      };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/clinical-notes`, params);
      const data = await response.json();

      if (data.error) {
        setClinicalNotes([]);
        window.location.href = "/error";
      }

      setClinicalNotes(data.clinicalNotes);
    } catch (_) {
      setClinicalNotes([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo, encounterFilter ]);

  useEffect(() => {
    if (isVerified) {
      fetchNotes();
    } else {
      setClinicalNotes(dummyNotes);
    }
  }, [ isVerified, fetchNotes, yearFrom, yearTo, encounterFilter, refreshKey ]);

  const handleView = useCallback(async (note: ClinicalNote) => {
    try {
      const data = await fetchBinaryContent(organization.id, patientId || '', note.url, note.contentType);
      setViewingNote({ ...noteToViewableItem(note), content: data.data || '' });
    } catch (_) {}
  }, [ organization.id, patientId ]);

  const handleDownload = useCallback(async (note: ClinicalNote) => {
    try {
      await downloadItem(organization.id, patientId || '', noteToViewableItem(note));
    } catch (_) {}
  }, [ organization.id, patientId ]);

  const handleEmail = useCallback((note: ClinicalNote) => {
    setEmailNote(note);
  }, []);

  const handleUploadNote = useCallback(async (content: string, transcriptionType: string) => {
    if (loadingPatientData || !patientData || !encounterFilter) return;
    setSubmittingNote(true);
    try {
      const response = await apiPost(`/api/${organization.id}/upload-clinical-note/${patientId}`, {
        csn: encounterFilter.csn,
        identifier: patientData.primaryIdentifier,
        lastName: patientData.lastName,
        givenNames: patientData.givenNames,
        dob: patientData.dateOfBirth,
        transcriptionType: transcriptionType,
        practitioner: encounterFilter.practitioner,
        noteContent: content,
      });
      if (response.ok) {
        showToast('Clinical note submitted successfully', 'success');
        setUploadNoteOpen(false);
        fetchNotes();
      } else {
        showToast('Failed to submit clinical note', 'error');
      }
    } catch (_) {
      showToast('Failed to submit clinical note', 'error');
    } finally {
      setSubmittingNote(false);
    }
  }, [ organization.id, patientData, loadingPatientData, showToast, fetchNotes, encounterFilter, patientId ]);

  const tableRows = useMemo(() =>
    convertNotesToRows(clinicalNotes, isVerified, handleView, handleDownload, handleEmail),
    [ clinicalNotes, isVerified, handleView, handleDownload, handleEmail ]
  );

  return (
    <BlurredCard>
      {encounterFilter && (
        <ActiveTableFilter
          clearLabel="Show all clinical notes"
          onClear={onClearEncounterFilter}
        >
          Showing clinical notes for: <span className="font-medium text-gray-900">{encounterFilter.type}</span>
          {encounterFilter.date && encounterFilter.date !== 'Unknown date' && (
            <> — {new Date(encounterFilter.date).toLocaleDateString()}</>
          )}
          {encounterFilter.practitioner && <> — <PractitionerLink name={encounterFilter.practitioner?.name || "—"} practitionerId={encounterFilter.practitioner?.practitioner_id} /></>}
        </ActiveTableFilter>
      )}
      {isVerified && encounterFilter && (
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => setUploadNoteOpen(true)}
            disabled={loadingPatientData || !patientData}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md border border-brand-600 bg-brand-600 text-white
              hover:bg-brand-700 active:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1
              disabled:bg-gray-200 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Upload Clinical Note
          </button>
        </div>
      )}

      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Clinical Notes..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {clinicalNotes.length > 0 ? (
            <StaticDataTable
              tableId="notes-table"
              columns={noteColumns}
              rows={tableRows}
              updateUrlParams={false}
              trailingColumnKey="Actions"
              rowId={(item) => `${item.Type}-${item.Date}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <DocumentTextIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No clinical notes found</p>
            </div>
          )}
        </div>
      )}

      <Toast />

      {uploadNoteOpen && (
        <UploadClinicalNoteModal
          onClose={() => setUploadNoteOpen(false)}
          onSubmit={handleUploadNote}
          submitting={submittingNote}
        />
      )}

      {viewingNote && (
        <Modal title="Clinical Note" onClose={() => setViewingNote(null)}>
          <div className="border rounded-lg p-4 bg-gray-50 max-h-[60vh] overflow-auto">
            {viewingNote.contentType === 'text/html' ? (
              <div dangerouslySetInnerHTML={{ __html: viewingNote.content || '' }} />
            ) : (
              <pre className="whitespace-pre-wrap break-words">{viewingNote.content}</pre>
            )}
          </div>
        </Modal>
      )}

      {emailNote && (
        <EmailModal
          context={{ type: emailNote.name, date: emailNote.date, author: emailNote.author?.name || "—" }}
          item={noteToViewableItem(emailNote)}
          onClose={() => setEmailNote(null)}
        />
      )}
    </BlurredCard>
  );
};

export default NotesCard;
